'use client';

import React from 'react';
import { AlertCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * FieldError — the message a field owes the user, announced, in a box that was
 * already there.
 *
 * TWO FAILURES IT REMOVES.
 *
 *   1. THE FORM JUMPS. Every error node in this app was conditionally rendered,
 *      so the message appearing pushed the next field, the footer and the save
 *      button down the page at the exact moment the user was reading. The box is
 *      permanent now and 16px tall whether or not it holds anything. That makes
 *      the forms slightly taller and it is a trade the owner accepted.
 *
 *   2. NOTHING SAYS IT. `VersionEditorModal` and `CycleEditorModal` reddened a
 *      border and rendered a paragraph with no role, no `aria-invalid` and no
 *      `aria-describedby`, so a screen reader user got a colour change and
 *      silence. `feedback/page.tsx` rendered a bare `div`.
 *
 * `role="alert"` rather than a `role="status"` live region, because that is the
 * pattern this codebase already blessed at `HabitFormModal.tsx:271` and because
 * the source's shape, where the same node is both the `aria-describedby` target
 * and a polite live region, is read twice by several screen readers. One node,
 * one announcement, and the id is what the field points at.
 *
 * The node stays mounted while empty on purpose: a live region inserted at the
 * same moment its text arrives is a known way to lose the announcement, and an
 * empty `<p>` has no accessible name so it adds nothing to the reading order.
 *
 * LINE is 16px, from `inline-validation.tsx`. `leading-4` is the matching line
 * box, so one line of message occupies exactly the space reserved for it and a
 * second line grows the box rather than overflowing it.
 */
export const FIELD_ERROR_LINE_PX = 16;

export function FieldError({
  id,
  message,
  className,
}: {
  /** The id the field names in its `aria-describedby`. */
  id: string;
  message?: string | null;
  /** The site's own type treatment. Nothing here restyles an existing error. */
  className?: string;
}) {
  return (
    <p
      id={id}
      role="alert"
      className={cn(
        'mt-1 flex min-h-4 items-center gap-1 text-[10px] font-medium text-destructive',
        className,
      )}
      // The line height is a style, not a class, and that is deliberate.
      // Tailwind v4's font-size utilities also set a line-height, so a caller
      // passing its own `text-[11px]` for the site's type treatment silently
      // replaced `leading-4` and the box grew to 17px the moment a message
      // arrived. Measured by tools/component-polish-walk.mjs:
      // `16px empty vs 17px with a message`. A style declaration cannot be
      // outranked by a utility class.
      style={{ lineHeight: `${FIELD_ERROR_LINE_PX}px` }}
    >
      {message ? (
        <>
          <AlertCircle size={10} aria-hidden="true" className="shrink-0" />
          {message}
        </>
      ) : null}
    </p>
  );
}
